import { navigate } from 'gatsby-plugin-intl';
import React, { FC, createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useEffectOnce } from 'react-use';

import { useOnSessionUpdate } from '../hooks';
import { useSavedRedirectURL } from '../hooks/saved-redirect-url';
import { SessionContextType } from '../types';
import { refreshSession } from '../utils/login';
import { logout } from '../utils/logout';
import { getSessionStatus } from '../utils/session';

const defaultSessionState = getSessionStatus();

const SessionContext = createContext<SessionContextType>(defaultSessionState);

export const SessionProvider: FC = ({ children }) => {
  const [value, setValue] = useState<SessionContextType>(defaultSessionState);

  const updateSessionStatus = useCallback(() => {
    setValue(getSessionStatus());
  }, []);

  useEffectOnce(updateSessionStatus);

  useOnSessionUpdate(updateSessionStatus);

  useEffect(() => {
    const [isAuth, token] = defaultSessionState;

    if (isAuth && !token?.trim()) {
      refreshSession().catch(() => {
        logout();
      });
    }
  }, []);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
};

export const useSession = (): SessionContextType => {
  const sessionDetails = useContext(SessionContext);

  if (sessionDetails === undefined) {
    throw new Error('useSession must only be used within a SessionProvider');
  }

  return sessionDetails;
};

export const useSessionStartRedirect = (redirect: string): void => {
  const [isAuthenticated] = useSession();

  const [userUrlSession, clearUserUrlSession] = useSavedRedirectURL();

  useEffect(() => {
    if (isAuthenticated) {
      if (userUrlSession) {
        navigate(userUrlSession);
        clearUserUrlSession();
      } else {
        navigate(redirect);
      }
    }
  }, [isAuthenticated]);
};
